// The components with behaviour, Radix's primitives in shadcn's shapes:
// Dialog, Sheet, Popover, DropdownMenu, ContextMenu, Select, Tooltip, Tabs, Switch,
// Checkbox, Collapsible. Radix keeps focus, keys, dismissal and placement
// right; preact/compat runs it (the build aliases react to it, ui/build.mjs).
// Their parts render on the server too, closed, so a page built ahead
// hydrates them where they stand.
import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import {
  Checkbox as CheckboxPrimitive,
  Collapsible as CollapsiblePrimitive,
  ContextMenu as ContextPrimitive,
  Dialog as DialogPrimitive,
  DropdownMenu as MenuPrimitive,
  Popover as PopoverPrimitive,
  Select as SelectPrimitive,
  Switch as SwitchPrimitive,
  Tabs as TabsPrimitive,
  Tooltip as TooltipPrimitive,
} from "radix-ui";
import { cn } from "../cn";
import { Check, ChevronDown, ChevronUp, ChevronRight, Circle, X } from "../icons";

type P = { class?: string; className?: string; children?: ComponentChildren; [k: string]: any };
const cls = (base: string, p: P) => cn(base, p.class, p.className);
function rest(p: P) {
  const { class: _c, className: _n, ...r } = p;
  return r;
}

// Dialog
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogOverlay = (p: P) => <DialogPrimitive.Overlay data-slot="dialog-overlay" className={cls("ui-overlay", p)} {...rest(p)} />;
// `container` draws it inside an element of the page instead of over the
// whole page; `backdrop` is what lies behind it: dimmed, blurred, or none.
export function DialogContent({ showClose = true, container, backdrop = "dim", children, ...p }: P & { showClose?: boolean; container?: HTMLElement | null; backdrop?: "dim" | "blur" | "none" }) {
  return (
    <DialogPrimitive.Portal container={container ?? undefined}>
      {backdrop !== "none" && <DialogOverlay data-backdrop={backdrop} />}
      <DialogPrimitive.Content data-slot="dialog-content" className={cls("ui-dialog", p)} {...rest(p)}>
        {children}
        {showClose && <DialogPrimitive.Close className="ui-dialog-close" aria-label="Close"><X /></DialogPrimitive.Close>}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}
export const DialogHeader = (p: P) => <div data-slot="dialog-header" className={cls("ui-dialog-header", p)} {...rest(p)} />;
export const DialogFooter = (p: P) => <div data-slot="dialog-footer" className={cls("ui-dialog-footer", p)} {...rest(p)} />;
export const DialogTitle = (p: P) => <DialogPrimitive.Title data-slot="dialog-title" className={cls("ui-dialog-title", p)} {...rest(p)} />;
export const DialogDescription = (p: P) => <DialogPrimitive.Description data-slot="dialog-description" className={cls("ui-dialog-description", p)} {...rest(p)} />;

// Sheet: a dialog that slides in from an edge.
export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;
export function SheetContent({ side = "right", children, ...p }: P & { side?: "top" | "right" | "bottom" | "left" }) {
  return (
    <DialogPrimitive.Portal>
      <DialogOverlay />
      <DialogPrimitive.Content data-slot="sheet-content" data-side={side} className={cls("ui-sheet", p)} {...rest(p)}>
        {children}
        <DialogPrimitive.Close className="ui-dialog-close" aria-label="Close"><X /></DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}
export const SheetHeader = DialogHeader;
export const SheetTitle = DialogTitle;
export const SheetDescription = DialogDescription;
export const SheetBody = (p: P) => <div data-slot="sheet-body" className={cls("ui-sheet-body", p)} {...rest(p)} />;

// Popover
export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;
export const PopoverClose = PopoverPrimitive.Close;
export function PopoverContent({ align = "center", sideOffset = 4, ...p }: P) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content data-slot="popover-content" align={align} sideOffset={sideOffset} className={cls("ui-popover", p)} {...rest(p)} />
    </PopoverPrimitive.Portal>
  );
}

// DropdownMenu
export const DropdownMenu = MenuPrimitive.Root;
export const DropdownMenuTrigger = MenuPrimitive.Trigger;
export const DropdownMenuGroup = MenuPrimitive.Group;
export const DropdownMenuSub = MenuPrimitive.Sub;
export const DropdownMenuRadioGroup = MenuPrimitive.RadioGroup;
export function DropdownMenuContent({ sideOffset = 4, ...p }: P) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Content data-slot="dropdown-menu-content" sideOffset={sideOffset} className={cls("ui-menu", p)} {...rest(p)} />
    </MenuPrimitive.Portal>
  );
}
export const DropdownMenuItem = ({ inset, variant = "default", ...p }: P & { inset?: boolean; variant?: "default" | "destructive" }) =>
  <MenuPrimitive.Item data-slot="dropdown-menu-item" data-inset={inset || undefined} data-variant={variant} className={cls("ui-menu-item", p)} {...rest(p)} />;
export function DropdownMenuCheckboxItem({ children, ...p }: P) {
  return (
    <MenuPrimitive.CheckboxItem data-slot="dropdown-menu-checkbox-item" className={cls("ui-menu-item", p)} {...rest(p)}>
      <span className="ui-menu-indicator"><MenuPrimitive.ItemIndicator><Check /></MenuPrimitive.ItemIndicator></span>
      {children}
    </MenuPrimitive.CheckboxItem>
  );
}
export function DropdownMenuRadioItem({ children, ...p }: P) {
  return (
    <MenuPrimitive.RadioItem data-slot="dropdown-menu-radio-item" className={cls("ui-menu-item", p)} {...rest(p)}>
      <span className="ui-menu-indicator"><MenuPrimitive.ItemIndicator><Circle width="8" height="8" /></MenuPrimitive.ItemIndicator></span>
      {children}
    </MenuPrimitive.RadioItem>
  );
}
export const DropdownMenuLabel = ({ inset, ...p }: P & { inset?: boolean }) => <MenuPrimitive.Label data-slot="dropdown-menu-label" data-inset={inset || undefined} className={cls("ui-menu-label", p)} {...rest(p)} />;
export const DropdownMenuSeparator = (p: P) => <MenuPrimitive.Separator data-slot="dropdown-menu-separator" className={cls("ui-menu-separator", p)} {...rest(p)} />;
export const DropdownMenuShortcut = (p: P) => <span data-slot="dropdown-menu-shortcut" className={cls("ui-menu-shortcut", p)} {...rest(p)} />;
export function DropdownMenuSubTrigger({ inset, children, ...p }: P & { inset?: boolean }) {
  return (
    <MenuPrimitive.SubTrigger data-slot="dropdown-menu-sub-trigger" data-inset={inset || undefined} className={cls("ui-menu-item", p)} {...rest(p)}>
      {children}<ChevronRight style={{ marginLeft: "auto" }} />
    </MenuPrimitive.SubTrigger>
  );
}
export const DropdownMenuSubContent = (p: P) => <MenuPrimitive.Portal><MenuPrimitive.SubContent data-slot="dropdown-menu-sub-content" className={cls("ui-menu", p)} {...rest(p)} /></MenuPrimitive.Portal>;

// ContextMenu: the same menu, at the pointer, on a right click or a long press.
export const ContextMenu = ContextPrimitive.Root;
export const ContextMenuTrigger = ContextPrimitive.Trigger;
export const ContextMenuGroup = ContextPrimitive.Group;
export const ContextMenuContent = (p: P) =>
  <ContextPrimitive.Portal><ContextPrimitive.Content data-slot="context-menu-content" className={cls("ui-menu", p)} {...rest(p)} /></ContextPrimitive.Portal>;
export const ContextMenuItem = ({ inset, variant = "default", ...p }: P & { inset?: boolean; variant?: "default" | "destructive" }) =>
  <ContextPrimitive.Item data-slot="context-menu-item" data-inset={inset || undefined} data-variant={variant} className={cls("ui-menu-item", p)} {...rest(p)} />;
export const ContextMenuLabel = ({ inset, ...p }: P & { inset?: boolean }) => <ContextPrimitive.Label data-slot="context-menu-label" data-inset={inset || undefined} className={cls("ui-menu-label", p)} {...rest(p)} />;
export const ContextMenuSeparator = (p: P) => <ContextPrimitive.Separator data-slot="context-menu-separator" className={cls("ui-menu-separator", p)} {...rest(p)} />;
export const ContextMenuShortcut = (p: P) => <span data-slot="context-menu-shortcut" className={cls("ui-menu-shortcut", p)} {...rest(p)} />;

// Select
export const Select = SelectPrimitive.Root;
export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = SelectPrimitive.Value;
export function SelectTrigger({ size = "default", children, ...p }: P & { size?: "default" | "sm" }) {
  return (
    <SelectPrimitive.Trigger data-slot="select-trigger" data-size={size} className={cls("ui-select-trigger", p)} {...rest(p)}>
      {children}
      <SelectPrimitive.Icon asChild><ChevronDown /></SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}
export function SelectContent({ children, position = "popper", ...p }: P) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content data-slot="select-content" position={position} sideOffset={position === "popper" ? 4 : undefined} className={cls("ui-menu ui-select-content", p)} {...rest(p)}>
        <SelectPrimitive.ScrollUpButton className="ui-menu-item"><ChevronUp /></SelectPrimitive.ScrollUpButton>
        <SelectPrimitive.Viewport className="ui-select-viewport">{children}</SelectPrimitive.Viewport>
        <SelectPrimitive.ScrollDownButton className="ui-menu-item"><ChevronDown /></SelectPrimitive.ScrollDownButton>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}
export const SelectLabel = (p: P) => <SelectPrimitive.Label data-slot="select-label" className={cls("ui-menu-label", p)} {...rest(p)} />;
export function SelectItem({ children, ...p }: P) {
  return (
    <SelectPrimitive.Item data-slot="select-item" className={cls("ui-menu-item", p)} {...rest(p)}>
      <span className="ui-menu-indicator"><SelectPrimitive.ItemIndicator><Check /></SelectPrimitive.ItemIndicator></span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}
export const SelectSeparator = (p: P) => <SelectPrimitive.Separator data-slot="select-separator" className={cls("ui-menu-separator", p)} {...rest(p)} />;

// Tooltip: one provider at the root of an app, so the delay is shared.
export const TooltipProvider = ({ delayDuration = 300, ...p }: P) => <TooltipPrimitive.Provider delayDuration={delayDuration} {...p} />;
// Always controlled here. Left to itself, Radix announces "a tooltip
// opened" from an effect, which under Preact runs after this tooltip's own
// content is listening for that announcement, so it closed itself at once.
// Controlled, the announcement goes out from the pointer or focus event,
// before the content exists.
export function Tooltip({ open: openProp, defaultOpen, onOpenChange, ...p }: P & { open?: boolean; defaultOpen?: boolean; onOpenChange?: (open: boolean) => void }) {
  const [own, setOwn] = useState(defaultOpen ?? false);
  return <TooltipPrimitive.Root open={openProp ?? own} onOpenChange={(v: boolean) => { setOwn(v); onOpenChange?.(v); }} {...p} />;
}
export const TooltipTrigger = TooltipPrimitive.Trigger;
export function TooltipContent({ sideOffset = 4, children, ...p }: P) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content data-slot="tooltip-content" sideOffset={sideOffset} className={cls("ui-tooltip", p)} {...rest(p)}>
        {children}
        <TooltipPrimitive.Arrow className="ui-tooltip-arrow" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

// Tabs
export const Tabs = (p: P) => <TabsPrimitive.Root data-slot="tabs" className={cls("ui-tabs", p)} {...rest(p)} />;
export const TabsList = (p: P) => <TabsPrimitive.List data-slot="tabs-list" className={cls("ui-tabs-list", p)} {...rest(p)} />;
export const TabsTrigger = (p: P) => <TabsPrimitive.Trigger data-slot="tabs-trigger" className={cls("ui-tabs-trigger", p)} {...rest(p)} />;
export const TabsContent = (p: P) => <TabsPrimitive.Content data-slot="tabs-content" className={cls("ui-tabs-content", p)} {...rest(p)} />;

// Switch, Checkbox
export const Switch = (p: P) => (
  <SwitchPrimitive.Root data-slot="switch" className={cls("ui-switch", p)} {...rest(p)}>
    <SwitchPrimitive.Thumb data-slot="switch-thumb" className="ui-switch-thumb" />
  </SwitchPrimitive.Root>
);
export const Checkbox = (p: P) => (
  <CheckboxPrimitive.Root data-slot="checkbox" className={cls("ui-checkbox", p)} {...rest(p)}>
    <CheckboxPrimitive.Indicator data-slot="checkbox-indicator"><Check /></CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
);

// Collapsible
export const Collapsible = CollapsiblePrimitive.Root;
export const CollapsibleTrigger = CollapsiblePrimitive.Trigger;
export const CollapsibleContent = CollapsiblePrimitive.Content;
